// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';
import { fetchWVStreams } from '../utils/streamService';
import { Heart, Map, Star, MapPin } from 'lucide-react';

const Favorites = ({ setCurrentPage }) => {
  const [favoriteStreams, setFavoriteStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();

  useEffect(() => {
    loadFavorites();
  }, [currentUser]);

  const loadFavorites = async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      // Get favorite stream IDs
      const favQuery = query(
        collection(db, 'favorites'),
        where('userId', '==', currentUser.uid)
      );
      const favSnapshot = await getDocs(favQuery);
      const favoriteIds = favSnapshot.docs.map(doc => doc.data().streamId);

      if (favoriteIds.length === 0) {
        setFavoriteStreams([]);
        setLoading(false);
        return;
      }

      // Load all streams from WV GIS
      const allStreams = await fetchWVStreams();
      
      // Filter to only favorites
      const favorites = allStreams.filter(stream => 
        favoriteIds.includes(stream.id)
      );

      setFavoriteStreams(favorites);
    } catch (error) {
      console.error('Error loading favorites:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (streamId) => {
    if (!currentUser) return;

    try {
      const q = query(
        collection(db, 'favorites'),
        where('userId', '==', currentUser.uid),
        where('streamId', '==', streamId)
      );
      const querySnapshot = await getDocs(q);
      querySnapshot.docs.forEach(async (document) => {
        await deleteDoc(doc(db, 'favorites', document.id));
      });
      
      setFavoriteStreams(favoriteStreams.filter(stream => stream.id !== streamId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h2 className="page-header-title">
            <Heart size={32} />
            My Favorites
          </h2>
          <p className="page-header-subtitle">0 saved streams</p>
        </div>
        <div className="empty-state">
          <div style={{ fontSize: '3rem' }}>⏳</div>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-header-title">
          <Heart size={32} />
          My Favorites
        </h2>
        <p className="page-header-subtitle">{favoriteStreams.length} saved streams</p>
      </div>

      {favoriteStreams.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <Heart size={60} strokeWidth={1.5} />
          </div>
          <h3 className="empty-title">No favorites yet</h3>
          <p className="empty-description">
            Start exploring the map to save your favorite fishing spots
          </p>
          <button className="btn btn-green" onClick={() => setCurrentPage('map')}>
            <Map size={18} />
            Explore Map
          </button>
        </div>
      ) : (
        <div className="section">
          <div className="features-grid">
            {favoriteStreams.map(stream => (
              <div key={stream.id} className="feature-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                  <h3 className="feature-title" style={{ marginBottom: 0 }}>{stream.name}</h3>
                  <button
                    onClick={() => removeFavorite(stream.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      color: '#F59E0B'
                    }}
                  >
                    <Star size={20} fill="currentColor" />
                  </button>
                </div>
                <p className="feature-description">
                  <MapPin size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                  {stream.county}
                  {stream.mileage && ` • ${stream.mileage} miles`}
                </p>
                {stream.nearCity && (
                  <p className="feature-description" style={{ fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    Near {stream.nearCity}
                  </p>
                )}
                <div style={{ marginTop: '0.75rem' }}>
                  {stream.regType && (
                    <span className={`badge ${stream.regType.includes('Catch') ? 'badge-catch-release' : 'badge-general'}`}>
                      {stream.regType}
                    </span>
                  )}
                  {stream.stockCode && stream.stockCode !== 'Unknown' && (
                    <span className="badge badge-general" style={{ marginLeft: '0.5rem' }}>
                      {stream.stockCode}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Favorites;
